import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Chip,
  Container,
  IconButton,
  Divider,
  Popover,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Pagination,
  Select,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth as CalendarMonthIcon,
  GroupOff as GroupOffIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import PageHeader from './PageHeader';
import { getAuditImageBlob, getUnmatchedGroupsToday } from '../utils/api';
import { peek as peekAuditImage } from '../services/auditImageCache';
import LazyAuditImg from './LazyAuditImg';
import AuditImg from './AuditImg';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import { DateRangePicker } from 'react-date-range';
import { format, addDays } from 'date-fns';
import { Fade } from '@mui/material';
import { DayTimelineBar } from './AuditAttendanceGridLayout';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const primary = '#40518A';

const getUnmatchedAction = (camera) => String(camera || '').toUpperCase() === 'IN' ? 'check_in' : 'check_out';

const getUnmatchedTimelineEventKey = (event) => {
  if (!event) return '';
  return [
    event.snapshot_audit_id ? `audit:${event.snapshot_audit_id}` : 'audit:none',
    event.attendance_id ? `attendance:${event.attendance_id}` : 'attendance:none',
    `action:${event.action || 'evt'}`,
    `at:${event.created_at || ''}`,
  ].join('|');
};

const resolveUnmatchedEventImage = (event, snapshotCache, fallbackImage = '/user.jpg') => {
  if (!event) return fallbackImage;
  const auditId = event.snapshot_audit_id;
  const fromCache = auditId ? (snapshotCache?.[auditId] ?? peekAuditImage(auditId)) : undefined;
  if (fromCache !== undefined && fromCache !== null) {
    return fromCache;
  }
  return fallbackImage;
};

const UnmatchedGroupTimelineModal = ({ open, onClose, group, snapshotCache }) => {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoveredTimelineEventKey, setHoveredTimelineEventKey] = useState(null);

  useEffect(() => {
    if (!open) setHoveredTimelineEventKey(null);
  }, [open]);

  if (!group) return null;

  const unmatchedLogs = (group.unmatched_logs || [])
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const avatarLetter = String(group.group_id || '?').charAt(0).toUpperCase();
  const modalDateLabel = (() => {
    if (!unmatchedLogs.length) return 'No date';
    const firstDate = unmatchedLogs[0]?.created_at;
    const lastDate = unmatchedLogs[unmatchedLogs.length - 1]?.created_at;
    if (!firstDate || !lastDate) return 'No date';
    const firstKey = format(new Date(firstDate), 'yyyy-MM-dd');
    const lastKey = format(new Date(lastDate), 'yyyy-MM-dd');
    if (firstKey === lastKey) return format(new Date(firstDate), 'EEE, MMM dd, yyyy');
    return `${format(new Date(firstDate), 'MMM dd, yyyy')} - ${format(new Date(lastDate), 'MMM dd, yyyy')}`;
  })();

  const groupedDays = (() => {
    const groups = {};
    unmatchedLogs.forEach((log) => {
      const eventDate = new Date(log.created_at);
      const dateKey = format(eventDate, 'yyyy-MM-dd');
      const dateLabel = format(eventDate, 'EEE, MMM dd, yyyy');
      if (!groups[dateKey]) groups[dateKey] = { dateKey, dateLabel, events: [] };
      groups[dateKey].events.push({
        ...log,
        action: getUnmatchedAction(log.camera),
        snapshot_audit_id: log.audit_id,
      });
    });
    return Object.values(groups).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  })();

  const isTodayDate = (dateKey) => format(new Date(), 'yyyy-MM-dd') === dateKey;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobileModal}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 180 }}
      PaperProps={{ sx: { borderRadius: isMobileModal ? 0 : 3, overflow: 'hidden', bgcolor: '#fafbfc', maxHeight: isMobileModal ? '100vh' : '85vh' } }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar src={group.avatar || undefined} sx={{ width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 }, bgcolor: primary, fontWeight: 700 }}>
              {avatarLetter}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }} noWrap>
                Group {group.group_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {group.unmatched_logs_count || unmatchedLogs.length || 0} unmatched logs
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={modalDateLabel} sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto', maxHeight: isMobileModal ? 'calc(100vh - 160px)' : 'calc(85vh - 180px)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={{ xs: 1, sm: 0 }} sx={{ mb: 1.75 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 6, bgcolor: '#86efac', borderRadius: 0.5 }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Present</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 6, bgcolor: '#fecaca', borderRadius: 0.5 }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Away</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 8, height: 8, bgcolor: '#22c55e', borderRadius: '50%' }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>In</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 8, height: 8, bgcolor: '#f59e0b', borderRadius: '50%' }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Out</Typography></Stack>
          </Stack>
        </Stack>

        {groupedDays.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">No activity recorded for this period</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedDays.map((day) => {
              const daySortedEvents = [...day.events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              return (
                <Box key={day.dateKey} sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
                  <DayTimelineBar
                    date={day.dateLabel}
                    events={day.events}
                    isToday={isTodayDate(day.dateKey)}
                    snapshotCache={snapshotCache}
                    highlightEventKey={hoveredTimelineEventKey}
                    hideSummary={true}
                  />

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, mt: 0.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Related Images
                    </Typography>
                    <Chip size="small" label={daySortedEvents.length} sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />
                    <Chip size="small" label={day.dateLabel} sx={{ height: 20, fontSize: '0.62rem', bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
                  </Stack>

                  {daySortedEvents.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px dashed', borderColor: '#cbd5e1' }}>
                      <Typography variant="body2" color="text.secondary">No images for this date</Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {daySortedEvents.map((event, idx) => {
                        const isCheckIn = event.action === 'check_in';
                        return (
                          <Paper
                            key={`${day.dateKey}-${event.audit_id || `${event.action}-${event.created_at}-${idx}`}`}
                            variant="outlined"
                            onMouseEnter={() => setHoveredTimelineEventKey(getUnmatchedTimelineEventKey(event))}
                            onMouseLeave={() => setHoveredTimelineEventKey(null)}
                            sx={{
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              bgcolor: '#fff',
                              maxWidth: 170,
                            }}
                          >
                            <AuditImg
                              auditId={event.snapshot_audit_id || event.audit_id}
                              alt={String(event.camera || (isCheckIn ? 'IN' : 'OUT')).toUpperCase()}
                              anonymousImage="/user.jpg"
                              sx={{ width: '100%', height: 120, display: 'block', bgcolor: '#f3f4f6' }}
                              imgSx={{ width: '100%', height: 120, objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                            />
                            <Box sx={{ p: 1.1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: 'block',
                                  fontWeight: 700,
                                  color: '#111827',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.15,
                                  mb: 0.9,
                                }}
                                noWrap
                              >
                                {formatDate(event.created_at, true, false)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontSize: '0.72rem', mb: 0.75 }}
                                noWrap
                              >
                                {formatDateOnly(event.created_at)}
                              </Typography>
                              <Chip
                                size="small"
                                label={isCheckIn ? 'IN' : 'OUT'}
                                sx={{
                                  height: 22,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  bgcolor: isCheckIn ? '#dcfce7' : '#fee2e2',
                                  color: isCheckIn ? '#166534' : '#991b1b',
                                }}
                              />
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

const UnmatchedGroups = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const GROUPS_PAGE_SIZE = 25;
  const [loading, setLoading] = useState(false);
  const [groupsData, setGroupsData] = useState({ groups: [], total_groups: 0, date: '', pagination: null });
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsLimit, setGroupsLimit] = useState(GROUPS_PAGE_SIZE);
  const [appliedRange, setAppliedRange] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [draftRange, setDraftRange] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [appliedStartTime, setAppliedStartTime] = useState('00:00');
  const [appliedEndTime, setAppliedEndTime] = useState('23:59');
  const [draftStartTime, setDraftStartTime] = useState('00:00');
  const [draftEndTime, setDraftEndTime] = useState('23:59');
  const [groupPages, setGroupPages] = useState({});
  const [snapshotCache] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const GROUP_PAGE_SIZE = 3;
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const show = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const buildDateTimeRange = useCallback((range, startTime, endTime) => {
    const startDate = range?.[0]?.startDate || new Date();
    const endDate = range?.[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const [istStartHour, istStartMinute] = parseTime(startTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(endTime, 23, 59);

    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, []);

  const loadGroups = useCallback(async (
    range = appliedRange,
    page = 1,
    limit = groupsLimit,
    startTime = appliedStartTime,
    endTime = appliedEndTime,
  ) => {
    try {
      setLoading(true);
      const { formattedStart, formattedEnd } = buildDateTimeRange(range, startTime, endTime);
      const offset = (page - 1) * limit;
      const payload = await getUnmatchedGroupsToday({
        start_time: formattedStart,
        end_time: formattedEnd,
        limit,
        offset,
      });
      const normalizedPayload = payload || { groups: [], total_groups: 0, date: '', pagination: null };
      setGroupsData(normalizedPayload);
      setGroupsPage(normalizedPayload.pagination?.current_page || page);
      setGroupsLimit(normalizedPayload.pagination?.limit || limit);
    } catch (e) {
      console.error(e);
      show('Failed to load unmatched groups', 'error');
    } finally {
      setLoading(false);
    }
  }, [appliedRange, groupsLimit, appliedStartTime, appliedEndTime, buildDateTimeRange]);

  useEffect(() => {
    loadGroups(appliedRange, 1);
  }, []);

  const quickSelects = [
    { label: 'Today', getRange: () => ({ startDate: new Date(), endDate: new Date() }) },
    { label: 'Yesterday', getRange: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
    { label: 'Last 7 Days', getRange: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
    { label: 'Last 30 Days', getRange: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
    { label: 'This Month', getRange: () => {
      const now = new Date();
      return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
    } },
    { label: 'Last Month', getRange: () => {
      const now = new Date();
      return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
    } },
  ];

  const dateRangeLabel = (() => {
    const startDate = appliedRange[0].startDate;
    const endDate = appliedRange[0].endDate;
    const sameDay = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');
    const sameYear = format(startDate, 'yyyy') === format(endDate, 'yyyy');
    if (sameDay) {
      return format(startDate, 'MMM d, yyyy');
    }
    if (sameYear) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  })();
  const displayTz = process.env.REACT_APP_DISPLAY_TIMEZONE || 'Asia/Kolkata';
  const getGroupDateMeta = (createdAt) => {
    if (!createdAt) return { key: 'unknown', label: 'Unknown Date' };
    const raw = String(createdAt).trim();
    const utcString = raw.endsWith('Z') ? raw : `${raw}Z`;
    const d = new Date(utcString);
    if (Number.isNaN(d.getTime())) return { key: 'unknown', label: 'Unknown Date' };
    const key = d.toLocaleDateString('en-CA', { timeZone: displayTz });
    const label = formatDateOnly(raw) || key;
    return { key, label };
  };

  const groupedByDate = (groupsData.groups || []).reduce((acc, group) => {
    const { key, label } = getGroupDateMeta(group?.created_at);
    if (!acc[key]) acc[key] = { label, groups: [] };
    acc[key].groups.push(group);
    return acc;
  }, {});
  const sortedGroupDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

  const handleOpenGroup = (group) => {
    setSelectedGroup(group || null);
    setOpenGroupDialog(Boolean(group));
  };
  const handleCloseGroup = () => {
    setOpenGroupDialog(false);
    setSelectedGroup(null);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        px: { xs: 1.5, md: 2 },
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <PageHeader
          title="Unmatched Face Groups"
          subtitle="Grouped Unmatched Logs"
          icon={null}
        />
      </Box>

      <Box sx={{ mt: -0.5, mb: 1, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          <Box
            onClick={(e) => {
              setDraftRange(appliedRange);
              setDraftStartTime(appliedStartTime);
              setDraftEndTime(appliedEndTime);
              setDatePickerAnchor(e.currentTarget);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              py: 1.2,
              px: 1.2,
              cursor: 'pointer',
              minWidth: 240,
              width: 240,
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
            }}
          >
            <CalendarMonthIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
              {dateRangeLabel}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={() => setDatePickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { maxWidth: '100vw', overflow: 'auto' },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ p: 2, borderRight: { md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              QUICK SELECT
            </Typography>
            <Stack spacing={0.5}>
              {quickSelects.map((q) => (
                <Button
                  key={q.label}
                  size="small"
                  variant="text"
                  onClick={() => {
                    const r = q.getRange();
                    setDraftRange([{ startDate: r.startDate, endDate: r.endDate, key: 'selection' }]);
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {q.label}
                </Button>
              ))}
            </Stack>
          </Box>
          <Box
            sx={{
              '& .rdrCalendarWrapper': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              },
              '& .rdrMonth': {
                width: { xs: '280px', sm: '300px' },
              },
            }}
          >
            <DateRangePicker
              ranges={draftRange}
              onChange={(ranges) => setDraftRange([ranges.selection])}
              moveRangeOnFirstSelection={false}
              months={isMobile ? 1 : 2}
              direction={isMobile ? 'vertical' : 'horizontal'}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={new Date()}
              rangeColors={['#40518A']}
            />
            <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                label="Start Time"
                type="time"
                size="small"
                value={draftStartTime}
                onChange={(e) => setDraftStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                value={draftEndTime}
                onChange={(e) => setDraftEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
              <Button
                size="small"
                onClick={() => {
                  const today = new Date();
                  setDraftRange([{ startDate: today, endDate: today, key: 'selection' }]);
                  setDraftStartTime('00:00');
                  setDraftEndTime('23:59');
                }}
                variant="outlined"
                startIcon={<RefreshIcon />}
              >
                Reset
              </Button>
              <Button size="small" variant="outlined" onClick={() => setDatePickerAnchor(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setAppliedRange(draftRange);
                  setAppliedStartTime(draftStartTime);
                  setAppliedEndTime(draftEndTime);
                  setGroupPages({});
                  setGroupsPage(1);
                  setDatePickerAnchor(null);
                  loadGroups(draftRange, 1, groupsLimit, draftStartTime, draftEndTime);
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>

      {loading ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {Array.from({ length: 9 }).map((_, idx) => (
            <Paper
              key={`unmatched-skeleton-${idx}`}
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: '#E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: 360, md: 370 },
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 2.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Skeleton variant="rounded" width={140} height={24} />
                  <Skeleton variant="rounded" width={170} height={24} />
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1.5,
                  }}
                >
                  {Array.from({ length: 3 }).map((__, i) => (
                    <Box key={`tile-skel-${idx}-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ position: 'relative', height: { xs: 80, sm: 100, md: 110 } }}>
                        <Skeleton variant="rectangular" width="100%" height="100%" />
                      </Box>
                      <Skeleton variant="text" width="70%" />
                      <Skeleton variant="text" width="45%" />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (groupsData.groups || []).length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            background: '#fafafa',
            flex: 1,
            minHeight: 240,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GroupOffIcon sx={{ fontSize: 42, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No unmatched groups found for selected range
          </Typography>
          <Typography variant="body2" color="text.secondary">
            New unmatched faces will appear here once recognized.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {sortedGroupDates.map((groupDate) => (
            <Box key={groupDate}>
              <Typography variant="subtitle2" sx={{ m: 1, fontWeight: 700, color: 'text.primary', fontSize: '1.2rem' }}>
                {groupedByDate[groupDate].label}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                }}
              >
                {groupedByDate[groupDate].groups.map((group) => {
            const unmatchedLogs = group.unmatched_logs || [];
            const totalPages = Math.max(1, Math.ceil(unmatchedLogs.length / GROUP_PAGE_SIZE));
            const currentPage = Math.min(groupPages[group.group_id] || 1, totalPages);
            const start = (currentPage - 1) * GROUP_PAGE_SIZE;
            const pageUnmatchedLogs = unmatchedLogs.slice(start, start + GROUP_PAGE_SIZE);
            return (
              <Box key={group.group_id} sx={{ display: 'flex' }}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    width: '100%',
                    height: '100%',
                    borderTop: `3px solid #40518A`,
                    minHeight: { xs: 360, md: 370 },
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
                    backgroundColor: '#fff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
                      transform: 'translateY(-4px)',
                      borderColor: '#40518A',
                    },
                    '&:active': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleOpenGroup(group)}
                >
                  <CardContent sx={{ p: 2.2, display: 'flex', flexDirection: 'column', gap: 1.4, height: '100%', overflow: 'hidden' }}>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar src={group.avatar} alt={group.group_id} sx={{ width: 40, height: 40 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                          Group : {group.group_id}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" alignItems="center" useFlexGap>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Created: ${formatDate(group.created_at, true, false) || '-'}`}
                        sx={{
                          borderColor: '#2e7d32',
                          color: '#2e7d32',
                          fontWeight: 600,
                          height: 25,
                        }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Last Updated: ${formatDate(group.last_updated_at, true, false) || '-'}`}
                        sx={{
                          borderColor: '#b71c1c',
                          color: '#b71c1c',
                          fontWeight: 600,
                          height: 25,
                        }}
                      />
                    </Stack>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Unmatched Logs
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          disabled={currentPage <= 1}
                          onClick={() => setGroupPages((prev) => ({ ...prev, [group.group_id]: Math.max(1, currentPage - 1) }))}
                          sx={{
                            width: 28,
                            height: 28,
                            color: '#9AA0AA',
                            bgcolor: currentPage <= 1 ? 'transparent' : 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' },
                          }}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center', fontWeight: 700 }}>
                          {currentPage}/{totalPages}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={currentPage >= totalPages}
                          onClick={() => setGroupPages((prev) => ({ ...prev, [group.group_id]: Math.min(totalPages, currentPage + 1) }))}
                          sx={{
                            width: 28,
                            height: 28,
                            color: '#9AA0AA',
                            bgcolor: currentPage >= totalPages ? 'transparent' : 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' },
                          }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1.5,
                        minHeight: 0,
                        maxHeight: 190,
                        alignContent: 'flex-start',
                        overflow: 'hidden',
                      }}
                    >
                      {[...pageUnmatchedLogs, ...Array(Math.max(0, GROUP_PAGE_SIZE - pageUnmatchedLogs.length)).fill(null)].map((unmatchedLog, idx) => (
                        <Paper
                          key={unmatchedLog ? unmatchedLog.audit_id : `placeholder-${idx}`}
                          variant="outlined"
                          sx={{
                            borderRadius: { xs: 1.5, sm: 2 },
                            width: '100%',
                            borderColor: '#D1D5DB',
                            visibility: unmatchedLog ? 'visible' : 'hidden',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                          }}
                        >
                          {unmatchedLog && (
                            <>
                              <Box
                                sx={{
                                  position: 'relative',
                                  height: { xs: 80, sm: 100, md: 110 },
                                  background: '#0f172a',
                                  flexShrink: 0,
                                }}
                              >
                                <LazyAuditImg
                                  auditId={unmatchedLog.audit_id}
                                  alt={unmatchedLog.audit_id}
                                  anonymousImage="/user.jpg"
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                  }}
                                  imgSx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    zIndex: 1,
                                    height: { xs: 18, sm: 20 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: { xs: 1, sm: 1.2 },
                                    fontWeight: 700,
                                    fontSize: { xs: '0.62rem', sm: '0.70rem' },
                                    letterSpacing: 0.3,
                                    bgcolor: String(unmatchedLog.camera || '').toUpperCase() === 'IN' ? '#dcfce7CF' : '#fee2e2CF',
                                    color: String(unmatchedLog.camera || '').toUpperCase() === 'IN' ? '#166534DF' : '#991b1bDF',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    borderBottomLeftRadius: '10px',
                                    borderBottomRightRadius: { xs: 1.5, sm: 2 },
                                    borderTopLeftRadius: { xs: 1.5, sm: 2 },
                                    whiteSpace: 'nowrap',
                                    visibility: 'visible',
                                  }}
                                >
                                  {String(unmatchedLog.camera || '-').toUpperCase()}
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  p: { xs: 0.75, sm: 1 },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  gap: 0.35,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, lineHeight: 1.3 }}
                                >
                                  {formatDate(unmatchedLog.created_at, true, false)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }, lineHeight: 1.2 }}
                                >
                                  {formatDateOnly(unmatchedLog.created_at)}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
              </Box>
            </Box>
          ))}
        </Stack>
        
      )}
      {!loading && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 2, flexWrap: 'wrap', gap: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Showing {(groupsData.pagination?.total || 0) === 0 ? 0 : (groupsData.pagination?.offset || 0) + 1}
              -{Math.min((groupsData.pagination?.offset || 0) + (groupsData.pagination?.limit || groupsLimit), groupsData.pagination?.total || 0)}
              {' '}of {groupsData.pagination?.total || groupsData.total_groups || 0} results
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
              <Select
                size="small"
                value={groupsLimit}
                disabled={(groupsData.pagination?.total || groupsData.total_groups || 0) === 0}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setGroupsLimit(newLimit);
                  setGroupsPage(1);
                  loadGroups(appliedRange, 1, newLimit);
                }}
                sx={{ minWidth: 90 }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={75}>75</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Stack>
          </Stack>
          <Pagination
            color="primary"
            page={groupsPage}
            count={Math.max(1, groupsData.pagination?.total_pages || Math.ceil((groupsData.total_groups || 0) / groupsLimit))}
            disabled={(groupsData.pagination?.total || groupsData.total_groups || 0) === 0}
            onChange={(_, page) => {
              setGroupsPage(page);
              loadGroups(appliedRange, page, groupsLimit);
            }}
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      <UnmatchedGroupTimelineModal
        open={openGroupDialog}
        onClose={handleCloseGroup}
        group={selectedGroup}
        snapshotCache={snapshotCache}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UnmatchedGroups;
